/* The counterparty list's filters by the company's own fields — `List.js`'s "Additional fields"
   block. The definitions are `GET excalibur/counterparty-fields/?is_filterable=true` (loaded once):
   `[{id, code, label, type, options}]`, type boolean | choice | number, `options` (choice only)
   `{id, label}`, deleted ones left out.

   The list takes, ANDed with each other and with every other filter:
     cf__<code>=true|false                      boolean
     cf__<code>=<option_id>[,<option_id>…]      choice — any of them
     cf__<code>__gte=<n> / cf__<code>__lte=<n>  number — inclusive bounds
   and answers anything else (unknown code, a field that is not filterable, a value that does not
   parse) with a 400 for the whole request. So the URL is read as it comes (`parseCf`, syntax only),
   held back from the request until the definitions are in, and then cleaned (`sanitizeCf`): a value
   the definitions cannot vouch for never reaches the request — the list does the same to a broken
   id or an unknown sort. That includes a DELETED option: the definitions no longer list it, so a
   link carrying one loses it.

   State shape, for raw and clean alike: `{ [code]: { eq, gte, lte } }`, strings as they go to the
   URL — `eq` is "true"/"false" or comma-joined option ids, `gte`/`lte` numbers as text. */

import React from "react";
import { Col, Form, InputNumber, Select, Space, Typography } from "antd";

import { COUNTERPARTIES_PAGE } from "../../config/lang";
import { translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import { decimalSeparator } from "./customFields";

const CF_PREFIX = "cf__";
const FILTERABLE = ["boolean", "choice", "number"];
const NUMBER_RE = /^-?\d{1,11}(\.\d{1,4})?$/;

/** Every `cf__…` of a query string, by code — syntax only, before the definitions are known. */
export function parseCf(search) {
  const out = {};
  new URLSearchParams(search).forEach((value, name) => {
    if (!name.startsWith(CF_PREFIX)) return;
    let code = name.slice(CF_PREFIX.length);
    let op = "eq";
    if (code.endsWith("__gte")) [code, op] = [code.slice(0, -5), "gte"];
    else if (code.endsWith("__lte")) [code, op] = [code.slice(0, -5), "lte"];
    if (!code) return;
    (out[code] = out[code] || {})[op] = value;
  });
  return out;
}

/** Keep only what the definitions vouch for. */
export function sanitizeCf(defs, raw) {
  const out = {};
  (defs || []).forEach(field => {
    const entry = raw[field.code];
    if (!entry || !FILTERABLE.includes(field.type)) return;
    if (field.type === "boolean") {
      if (entry.eq === "true" || entry.eq === "false") out[field.code] = { eq: entry.eq };
    } else if (field.type === "choice") {
      const known = new Set((field.options || []).map(o => String(o.id)));
      const ids = [
        ...new Set(
          String(entry.eq || "")
            .split(",")
            .map(s => s.trim())
        ),
      ].filter(id => known.has(id));
      if (ids.length) out[field.code] = { eq: ids.join(",") };
    } else {
      const gte = NUMBER_RE.test(entry.gte || "") ? String(Number(entry.gte)) : null;
      const lte = NUMBER_RE.test(entry.lte || "") ? String(Number(entry.lte)) : null;
      /* A range that cannot match is dropped whole rather than sent. */
      if (gte !== null && lte !== null && Number(gte) > Number(lte)) return;
      if (gte !== null || lte !== null) out[field.code] = { ...(gte !== null ? { gte } : {}), ...(lte !== null ? { lte } : {}) };
    }
  });
  return out;
}

/** `[[name, value]]` — the same for the URL and for the request. */
export function cfParams(cf) {
  const out = [];
  Object.keys(cf)
    .sort()
    .forEach(code => {
      const { eq, gte, lte } = cf[code];
      if (eq) out.push([`${CF_PREFIX}${code}`, eq]);
      if (gte) out.push([`${CF_PREFIX}${code}__gte`, gte]);
      if (lte) out.push([`${CF_PREFIX}${code}__lte`, lte]);
    });
  return out;
}

const numberText = (value, lang) => new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: 4 }).format(Number(value));

/** One chip per field: "Так", "раз на тиждень, раз на місяць", "3–6", "від 3", "до 6". */
export function cfChips(defs, cf, lang, prefix) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  return (defs || [])
    .filter(field => cf[field.code])
    .map(field => {
      const { eq, gte, lte } = cf[field.code];
      let value;
      if (field.type === "boolean") value = t(eq === "true" ? "cf_yes" : "cf_no");
      else if (field.type === "choice") {
        const ids = eq.split(",");
        value = (field.options || [])
          .filter(o => ids.includes(String(o.id)))
          .map(o => o.label)
          .join(", ");
      } else if (gte && lte) value = `${numberText(gte, lang)}–${numberText(lte, lang)}`;
      else if (gte) value = t("cf_chip_from").replace("{n}", numberText(gte, lang));
      else value = t("cf_chip_to").replace("{n}", numberText(lte, lang));
      return { key: `cf:${field.code}`, id: field.code, label: `${prefix} · ${field.label}`, value };
    });
}

/** The block's controls, one grid cell per field — the point block's grid. */
export function CustomFieldControls({ defs, cf, typed, lang, colProps, onBool, onChoice, onNumber }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const separator = decimalSeparator(lang);
  return (defs || []).map(field => {
    const id = `cl-cf-${field.code}`;
    if (field.type === "boolean")
      return (
        <Col key={field.code} {...colProps}>
          {/* A Select, as every other control of the grid: one value or none, with a clear button.
              A pair of checkable tags could be both on, which means nothing here. */}
          <Form.Item label={field.label} htmlFor={id}>
            <Select
              id={id}
              allowClear
              placeholder={t("cf_any")}
              value={cf[field.code]?.eq ?? undefined}
              options={[
                { value: "true", label: t("cf_yes") },
                { value: "false", label: t("cf_no") },
              ]}
              onChange={value => onBool(field.code, value ?? null)}
            />
          </Form.Item>
        </Col>
      );
    if (field.type === "choice")
      return (
        <Col key={field.code} {...colProps}>
          <Form.Item label={field.label} htmlFor={id}>
            {/* The entity pickers' chrome (`es`, one chip + "+N"), over the field's own options. */}
            <Select
              id={id}
              className="es"
              mode="multiple"
              allowClear
              maxTagCount={1}
              maxTagPlaceholder={rest => `+${rest.length}`}
              placeholder={field.label}
              optionFilterProp="label"
              value={cf[field.code]?.eq ? cf[field.code].eq.split(",") : []}
              options={(field.options || []).map(o => ({ value: String(o.id), label: o.label }))}
              onChange={ids => onChoice(field.code, ids)}
            />
          </Form.Item>
        </Col>
      );
    const range = typed[field.code] || {};
    const invalid = range.gte != null && range.lte != null && Number(range.gte) > Number(range.lte);
    return (
      <Col key={field.code} {...colProps}>
        <Form.Item label={field.label} htmlFor={`${id}-gte`} validateStatus={invalid ? "error" : undefined} help={invalid ? t("cf_range_invalid") : undefined}>
          <Space.Compact block>
            <InputNumber
              id={`${id}-gte`}
              style={{ width: "50%" }}
              placeholder={t("cf_from")}
              aria-label={`${field.label}: ${t("cf_from")}`}
              decimalSeparator={separator}
              status={invalid ? "error" : undefined}
              value={range.gte ?? null}
              onChange={value => onNumber(field.code, "gte", value)}
            />
            <InputNumber
              id={`${id}-lte`}
              style={{ width: "50%" }}
              placeholder={t("cf_to")}
              aria-label={`${field.label}: ${t("cf_to")}`}
              decimalSeparator={separator}
              status={invalid ? "error" : undefined}
              value={range.lte ?? null}
              onChange={value => onNumber(field.code, "lte", value)}
            />
          </Space.Compact>
        </Form.Item>
      </Col>
    );
  });
}

/** The block header's list of field names, as the point block lists its own. */
export const CfHeaderNames = ({ defs }) => <Typography.Text type="secondary">{(defs || []).map(f => f.label).join(" · ")}</Typography.Text>;

export const hasCf = cf => Object.keys(cf || {}).length > 0;
